import { useCallback, useEffect, useRef, useState } from 'react';

export interface ViewportStage {
  scale: number;
  offsetX: number;
  offsetY: number;
}

/**
 * Keeps high-frequency viewport previews out of React and Zustand.
 *
 * Konva owns the visual transform while a pan/zoom gesture is active. React
 * and the diagram store receive one settled value after the gesture, so
 * moving the viewport never reconciles the full canvas tree per pointer event.
 */
export function useViewportStageController(
  storeStage: ViewportStage,
  setStage: (next: ViewportStage) => void,
  debounceMs = 120,
  beforeCommit?: (next: ViewportStage) => void,
): {
  viewportStage: ViewportStage;
  previewViewportStage: (next: ViewportStage) => void;
  getLatestViewportStage: () => ViewportStage;
  commitViewportStageDebounced: (next: ViewportStage) => void;
  commitViewportStageImmediate: (next: ViewportStage) => void;
} {
  const [viewportStage, setViewportStage] = useState(storeStage);
  const latestViewportStageRef = useRef(storeStage);
  const pendingViewportStageRef = useRef<ViewportStage | null>(null);
  const viewportCommitTimerRef = useRef<number | null>(null);
  const externalScale = storeStage.scale;
  const externalOffsetX = storeStage.offsetX;
  const externalOffsetY = storeStage.offsetY;

  const previewViewportStage = useCallback((next: ViewportStage) => {
    latestViewportStageRef.current = next;
  }, []);
  const getLatestViewportStage = useCallback(
    () => latestViewportStageRef.current,
    [],
  );

  useEffect(() => {
    const externalStage = {
      scale: externalScale,
      offsetX: externalOffsetX,
      offsetY: externalOffsetY,
    };
    const latest = latestViewportStageRef.current;
    if (
      latest.scale === externalStage.scale
      && latest.offsetX === externalStage.offsetX
      && latest.offsetY === externalStage.offsetY
    ) {
      return;
    }
    if (viewportCommitTimerRef.current !== null) {
      window.clearTimeout(viewportCommitTimerRef.current);
      viewportCommitTimerRef.current = null;
    }
    pendingViewportStageRef.current = null;
    latestViewportStageRef.current = externalStage;
    beforeCommit?.(externalStage);
    setViewportStage(externalStage);
  }, [beforeCommit, externalOffsetX, externalOffsetY, externalScale]);

  useEffect(() => () => {
    if (viewportCommitTimerRef.current !== null) {
      window.clearTimeout(viewportCommitTimerRef.current);
    }
  }, []);

  const commitViewportStageDebounced = useCallback((next: ViewportStage) => {
    previewViewportStage(next);
    pendingViewportStageRef.current = next;
    if (viewportCommitTimerRef.current !== null) {
      window.clearTimeout(viewportCommitTimerRef.current);
    }
    viewportCommitTimerRef.current = window.setTimeout(() => {
      viewportCommitTimerRef.current = null;
      const settled = pendingViewportStageRef.current;
      pendingViewportStageRef.current = null;
      if (!settled) return;
      beforeCommit?.(settled);
      setViewportStage(settled);
      setStage(settled);
    }, debounceMs);
  }, [beforeCommit, debounceMs, previewViewportStage, setStage]);

  const commitViewportStageImmediate = useCallback((next: ViewportStage) => {
    previewViewportStage(next);
    pendingViewportStageRef.current = null;
    if (viewportCommitTimerRef.current !== null) {
      window.clearTimeout(viewportCommitTimerRef.current);
      viewportCommitTimerRef.current = null;
    }
    beforeCommit?.(next);
    setViewportStage(next);
    setStage(next);
  }, [beforeCommit, previewViewportStage, setStage]);

  return {
    viewportStage,
    previewViewportStage,
    getLatestViewportStage,
    commitViewportStageDebounced,
    commitViewportStageImmediate,
  };
}
